import React, { useState, useEffect } from 'react';
import axios from 'axios';
import RecurringPaymentForm from './RecurringPaymentForm';
import ModificationHistoryModal from './ModificationHistoryModal';
import { formatDateSGT } from '../utils/dateUtils';
import '../assets/RecurringPaymentManager.css';

// Use production URL if on production domain, otherwise use env var or localhost
const getApiBaseUrl = () => {
  if (window.location.hostname === 'portal.jjtutorialsg.com') {
    return 'https://jjt-tutor-portal.onrender.com/api';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Derive the display status of a recurring payment from its date window and the
 * stored isActive flag, so the badge always matches reality:
 *   - 'scheduled' : start date is after the current month (not started yet)
 *   - 'inactive'  : manually paused (isActive=false) OR expired (end before now)
 *   - 'active'    : currently in its date window and live
 * Month-based comparison (start/end of the current calendar month) matches how
 * invoices apply recurring payments per month.
 */
const getRecurringStatus = (payment) => {
  const now = new Date();
  const curMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const curMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

  const start = payment.startDate ? new Date(payment.startDate) : null;
  const end = payment.endDate ? new Date(payment.endDate) : null;

  // Not started yet: starts after this month ends.
  if (start && start > curMonthEnd) return 'scheduled';

  // Expired or manually paused.
  if (!payment.isActive) return 'inactive';
  if (end && end < curMonthStart) return 'inactive';

  return 'active';
};

const STATUS_LABEL = { scheduled: 'Scheduled', active: 'Active', inactive: 'Inactive' };

const RecurringPaymentManager = ({ tutors, onPaymentChanged }) => {
  const [recurringPayments, setRecurringPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filters - now arrays for multi-select
  const [filters, setFilters] = useState({
    tutorIds: [],
    types: [],
    statuses: [] // can include 'active', 'inactive'
  });
  
  // Dropdown open states
  const [openDropdown, setOpenDropdown] = useState(null);
  
  // UI State
  const [showForm, setShowForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [historyPayment, setHistoryPayment] = useState(null);

  // Load recurring payments on mount and when filters change
  useEffect(() => {
    fetchRecurringPayments();
  }, [filters]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.multi-select-dropdown')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const fetchRecurringPayments = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Fetch all recurring payments first
      const response = await axios.get(
        `${API_BASE_URL}/recurring-payments`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      let payments = response.data || [];
      
      // Apply client-side filters
      if (filters.tutorIds.length > 0) {
        payments = payments.filter(p => filters.tutorIds.includes(p.tutor._id));
      }
      
      if (filters.types.length > 0) {
        payments = payments.filter(p => filters.types.includes(p.type));
      }
      
      if (filters.statuses.length > 0) {
        payments = payments.filter(p => filters.statuses.includes(getRecurringStatus(p)));
      }

      setRecurringPayments(payments);
    } catch (err) {
      console.error('Error fetching recurring payments:', err);
      setError('Failed to load recurring payments');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterToggle = (field, value) => {
    const currentValues = filters[field];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    
    setFilters({ ...filters, [field]: newValues });
  };

  const handleClearFilters = () => {
    setFilters({
      tutorIds: [],
      types: [],
      statuses: []
    });
  };
  
  const toggleDropdown = (dropdown) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const handleAddNew = () => {
    setEditingPayment(null);
    setShowForm(true);
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setShowForm(true);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingPayment(null);
  };

  const handleFormSubmit = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingPayment) {
        // Update existing recurring payment
        await axios.put(
          `${API_BASE_URL}/recurring-payments/${editingPayment._id}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      } else {
        // Create new recurring payment
        await axios.post(
          `${API_BASE_URL}/recurring-payments`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      // Refresh the list
      await fetchRecurringPayments();
      
      // Notify parent so the tutor invoice table can refresh its data
      onPaymentChanged?.();

      // Close form
      setShowForm(false);
      setEditingPayment(null);
    } catch (err) {
      console.error('Error saving recurring payment:', err);
      throw err; // Let the form handle the error
    }
  };

  const handleToggleStatus = async (payment) => {
    if (!window.confirm(`Are you sure you want to ${payment.isActive ? 'deactivate' : 'activate'} this recurring payment?`)) {
      return;
    }

    setTogglingId(payment._id);

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_BASE_URL}/recurring-payments/${payment._id}/toggle`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Refresh the list
      await fetchRecurringPayments();
      // Notify parent (tutor invoice table needs to reflect the change)
      onPaymentChanged?.();
    } catch (err) {
      console.error('Error toggling recurring payment status:', err);
      // Surface the server's message (e.g. "Cannot activate: the end date has
      // already passed...") so the refusal is explained, not a generic error.
      setError(err.response?.data?.message || 'Failed to update payment status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recurring payment? This action cannot be undone.')) {
      return;
    }

    setDeletingId(id);

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/recurring-payments/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Refresh the list
      await fetchRecurringPayments();
      // Notify parent (tutor invoice table needs to reflect the change)
      onPaymentChanged?.();
    } catch (err) {
      console.error('Error deleting recurring payment:', err);
      setError('Failed to delete recurring payment');
    } finally {
      setDeletingId(null);
    }
  };

  const handleViewHistory = (payment) => {
    setHistoryPayment(payment);
  };

  const handleCloseHistory = () => {
    setHistoryPayment(null);
  };

  const formatCurrency = (amount) => {
    return `${amount.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Shared SGT helper; dates stay pinned to Singapore time but without the
    // '(SGT)' suffix in the table.
    return formatDateSGT(dateString);
  };

  const formatType = (type) => {
    const typeMap = {
      'admin_stipend': 'Admin Stipend',
      'performance_bonus': 'Performance Bonus',
      'custom': 'Custom'
    };
    return typeMap[type] || type;
  };

  const paymentTypes = [
    { value: 'admin_stipend', label: 'Admin Stipend' },
    { value: 'performance_bonus', label: 'Performance Bonus' },
    { value: 'custom', label: 'Custom' }
  ];

  if (showForm) {
    return (
      <RecurringPaymentForm
        tutors={tutors}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
        initialData={editingPayment}
        isEdit={!!editingPayment}
      />
    );
  }

  return (
    <div className="recurring-payment-manager">
      <div className="manager-header">
        <h2>Recurring Payments</h2>
        <button className="btn-add-new" onClick={handleAddNew}>
          + Add New Recurring Payment
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          {/* Tutor Filter */}
          <div className="filter-group">
            <label>Tutor ({filters.tutorIds.length} selected)</label>
            <div className="multi-select-dropdown">
              <button 
                className="dropdown-toggle"
                onClick={() => toggleDropdown('tutors')}
              >
                {filters.tutorIds.length === 0 ? 'All Tutors' : `${filters.tutorIds.length} selected`}
                <span className="dropdown-arrow">{openDropdown === 'tutors' ? '▲' : '▼'}</span>
              </button>
              {openDropdown === 'tutors' && (
                <div className="dropdown-menu">
                  {tutors.map(tutor => (
                    <label key={tutor._id} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={filters.tutorIds.includes(tutor._id)}
                        onChange={() => handleFilterToggle('tutorIds', tutor._id)}
                      />
                      <span>{tutor.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Type Filter */}
          <div className="filter-group">
            <label>Type ({filters.types.length} selected)</label>
            <div className="multi-select-dropdown">
              <button 
                className="dropdown-toggle"
                onClick={() => toggleDropdown('types')}
              >
                {filters.types.length === 0 ? 'All Types' : `${filters.types.length} selected`}
                <span className="dropdown-arrow">{openDropdown === 'types' ? '▲' : '▼'}</span>
              </button>
              {openDropdown === 'types' && (
                <div className="dropdown-menu">
                  {paymentTypes.map(type => (
                    <label key={type.value} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={filters.types.includes(type.value)}
                        onChange={() => handleFilterToggle('types', type.value)}
                      />
                      <span>{type.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Status Filter */}
          <div className="filter-group">
            <label>Status ({filters.statuses.length} selected)</label>
            <div className="multi-select-dropdown">
              <button 
                className="dropdown-toggle"
                onClick={() => toggleDropdown('statuses')}
              >
                {filters.statuses.length === 0 ? 'All Status' : `${filters.statuses.length} selected`}
                <span className="dropdown-arrow">{openDropdown === 'statuses' ? '▲' : '▼'}</span>
              </button>
              {openDropdown === 'statuses' && (
                <div className="dropdown-menu">
                  <label className="dropdown-item">
                    <input
                      type="checkbox"
                      checked={filters.statuses.includes('active')}
                      onChange={() => handleFilterToggle('statuses', 'active')}
                    />
                    <span>Active</span>
                  </label>
                  <label className="dropdown-item">
                    <input
                      type="checkbox"
                      checked={filters.statuses.includes('scheduled')}
                      onChange={() => handleFilterToggle('statuses', 'scheduled')}
                    />
                    <span>Scheduled</span>
                  </label>
                  <label className="dropdown-item">
                    <input
                      type="checkbox"
                      checked={filters.statuses.includes('inactive')}
                      onChange={() => handleFilterToggle('statuses', 'inactive')}
                    />
                    <span>Inactive</span>
                  </label>
                </div>
              )}
            </div>
          </div>

          <button className="btn-clear-filters" onClick={handleClearFilters}>
            Clear All Filters
          </button>
        </div>
      </div>

      {/* Results Summary */}
      {!loading && (
        <div className="results-summary">
          Showing {recurringPayments.length} recurring payment{recurringPayments.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading recurring payments...</p>
        </div>
      )}

      {/* Recurring Payments Table */}
      {!loading && recurringPayments.length > 0 && (
        <div className="table-container">
          <table className="recurring-payments-table">
            <thead>
              <tr>
                <th>Tutor</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Reason</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recurringPayments.map(payment => (
                <tr key={payment._id} className={!payment.isActive ? 'inactive-row' : ''}>
                  <td className="tutor-cell">
                    <div className="tutor-name">{payment.tutor?.name || 'Unknown'}</div>
                    {payment.tutor?.email && (
                      <div className="tutor-email">{payment.tutor.email}</div>
                    )}
                  </td>
                  <td className="amount-cell">
                    +{formatCurrency(payment.amount)}
                  </td>
                  <td>
                    <span className="type-badge">{formatType(payment.type)}</span>
                  </td>
                  <td className="reason-cell">
                    <div className="reason-text">{payment.reason}</div>
                  </td>
                  <td>{formatDate(payment.startDate)}</td>
                  <td>{formatDate(payment.endDate)}</td>
                  <td>
                    {(() => {
                      const st = getRecurringStatus(payment);
                      return (
                        <span className={`status-badge ${st}`}>
                          {STATUS_LABEL[st]}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="actions-cell">
                    <button
                      className="btn-edit"
                      onClick={() => handleEdit(payment)}
                      disabled={deletingId === payment._id || togglingId === payment._id}
                    >
                      Edit
                    </button>
                    {/* Activate shown only for genuinely INACTIVE (expired/paused)
                        payments. A 'scheduled' (future-start) payment will run on
                        its own, so it needs no manual activation. Deactivate button
                        was removed — payments stop via end date / auto-deactivation. */}
                    {getRecurringStatus(payment) === 'inactive' && (
                      <button
                        className="btn-toggle activate"
                        onClick={() => handleToggleStatus(payment)}
                        disabled={deletingId === payment._id || togglingId === payment._id}
                      >
                        {togglingId === payment._id ? 'Processing...' : 'Activate'}
                      </button>
                    )}
                    {payment.modificationHistory && payment.modificationHistory.length > 0 && (
                      <button
                        className="btn-history"
                        onClick={() => handleViewHistory(payment)}
                        disabled={deletingId === payment._id || togglingId === payment._id}
                      >
                        History
                      </button>
                    )}
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(payment._id)}
                      disabled={deletingId === payment._id || togglingId === payment._id}
                    >
                      {deletingId === payment._id ? 'Deleting...' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!loading && recurringPayments.length === 0 && (
        <div className="empty-state">
          <p>No recurring payments found</p>
          <button className="btn-add-first" onClick={handleAddNew}>
            Add Your First Recurring Payment
          </button>
        </div>
      )}

      {/* Modification History Modal */}
      {historyPayment && (
        <ModificationHistoryModal
          payment={historyPayment}
          onClose={handleCloseHistory}
        />
      )}
    </div>
  );
};

export default RecurringPaymentManager;
